import MetaTags from '../../../common/components/MetaTags';
import {
  Box,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  Container,
  Chip,
  List,
  ListItem,
  useTheme,
  useMediaQuery,
  Toolbar,
} from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchCustomerRestaurantAsync } from './customerRestaurantSlice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { TabContext, TabPanel } from '@mui/lab';
import MealItem from '../../../app/data/models/mealItem';
import { setCartRestaurant, updateProductQuantity } from '../cart/cartSlice';
import MealItemCard from './MealItemCard';
import { useTranslation } from 'react-i18next';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import { formatCurrency } from '../../../app/utils/formatters';
import QuantitySelector from '../../../app/components/QuantitySelector';
import { CartItemCustomization } from '../../../app/data/models/cart';
import MealItemCustomizationSelector from './MealItemCustomizationSelector';
import { useCustomizations } from '../../../hooks/useCustomizations';
import {
  isMenuAvailable,
  formatMenuTime,
  isMenuEnabledForToday,
} from '../../../app/utils/menuAvailability';
import Menu from '../../../app/data/models/menu';
// Import the location and hours component, but it's commented out for now
// import RestaurantLocationAndHours from './RestaurantLocationAndHours';

const RESTAURANT_TABLE_KEY = (restaurantId: string) =>
  `restaurant_${restaurantId}_table_number`;

const CustomerRestaurantPage = () => {
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [searchParams] = useSearchParams();
  const { restaurant, status } = useAppSelector(
    (state) => state.customerRestaurant
  );
  const dispatch = useAppDispatch();
  const { cart } = useAppSelector((state) => state.cart);
  const [selectedMenu, setSelectedMenu] = useState(`${0}`);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [availableMenus, setAvailableMenus] = useState<string[]>([]);

  // Handle table number from URL on component mount
  useEffect(() => {
    const tableParam = searchParams.get('table');
    if (tableParam) {
      const tableNumber = Number(tableParam);
      if (!isNaN(tableNumber)) {
        // Store table number with restaurant context
        sessionStorage.setItem(
          RESTAURANT_TABLE_KEY(restaurantId),
          tableNumber.toString()
        );
      }
    }
  }, [restaurantId, searchParams]);

  const onQuantityChange = (
    cartItemId: string,
    mealItem: MealItem,
    quantity: number,
    customizations?: CartItemCustomization[]
  ) => {
    const cartItem = {
      cartItemId,
      mealItemId: mealItem.id,
      pictureUrl: mealItem.pictureUrl || '/assets/images/meal-placeholder.svg',
      name: mealItem.name,
      price: mealItem.price,
      taxRate: mealItem.taxRate,
      customizations,
    };
    dispatch(updateProductQuantity({ cartItem, quantity, restaurantId }));
  };

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setSelectedMenu(`${newValue}`);
  };

  // Uncomment it when `RestaurantLocationAndHours` needs to be shown
  // const handleAddressClick = async (address: string) => {
  //   try {
  //     await navigator.clipboard.writeText(address);
  //     toast.dark(t('restaurant.address.copied'));
  //   } catch (error) {
  //     console.error('Failed to copy address:', error);
  //     // Fallback to opening maps if clipboard fails
  //     window.open(
  //       `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
  //       '_blank'
  //     );
  //   }
  // };

  useEffect(() => {
    dispatch(fetchCustomerRestaurantAsync(restaurantId));
  }, [restaurantId, dispatch]);

  useEffect(() => {
    if (restaurant) {
      dispatch(setCartRestaurant(restaurant));
    }
  }, [restaurant, dispatch]);

  useEffect(() => {
    const checkAvailability = () => {
      const available =
        restaurant?.menus
          ?.filter((menu) => isMenuAvailable(menu.availabilitySchedules))
          .map((menu) => menu.id) ?? [];
      setAvailableMenus(available);
    };

    checkAvailability(); // Initial check
    const interval = setInterval(checkAvailability, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [restaurant]);

  const ListViewMealItem = ({
    mealItem,
    isAvailable,
  }: {
    mealItem: MealItem;
    isAvailable: boolean;
  }) => {
    const cart = useAppSelector((state) => state.cart);
    const [imageError, setImageError] = useState(false);

    // Add check for individual meal item availability
    const isMealItemAvailable = isAvailable && mealItem.isAvailable !== false; // Check if meal item itself is available

    const quantity = cart.cart.orderItems
      .filter((item) => item.mealItemId === mealItem.id)
      .reduce((sum, item) => sum + item.quantity, 0);

    const {
      showCustomizations,
      setShowCustomizations,
      handleQuantityChange,
      handleAddCustomizations,
    } = useCustomizations(mealItem, (cartItemId, qty, customizations) =>
      onQuantityChange(cartItemId, mealItem, qty, customizations)
    );

    return (
      <>
        <ListItem
          divider
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            opacity: isMealItemAvailable ? 1 : 0.7,
            position: 'relative',
            '&:hover': isMealItemAvailable
              ? { bgcolor: 'action.hover' }
              : undefined,
          }}
        >
          {/* Content */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                mb: 0,
                lineHeight: 1.2,
              }}
            >
              {mealItem.name}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {formatCurrency(mealItem.price)}
              </Typography>
              {/* Review section - commented out temporarily
              {mealItem.rating && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  • {mealItem.rating}% ({mealItem.reviewCount})
                </Typography>
              )}
              */}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: '0.8rem',
                lineHeight: 1.4,
              }}
            >
              {mealItem.description}
            </Typography>
          </Box>

          {/* Image and Quantity Selector */}
          <Box sx={{ position: 'relative' }}>
            {imageError ? (
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'action.hover',
                  textAlign: 'center',
                  p: 1,
                  overflow: 'hidden',
                }}
                role="img"
                aria-label={mealItem.name}
              >
                <Typography variant="caption" color="text.secondary">
                  {mealItem.name}
                </Typography>
              </Box>
            ) : (
              <Box
                component="img"
                src={
                  mealItem.pictureUrl || '/assets/images/meal-placeholder.svg'
                }
                alt={mealItem.name}
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 1,
                  objectFit: 'cover',
                  filter: !isMealItemAvailable ? 'grayscale(100%)' : 'none',
                }}
                onError={() => setImageError(true)}
              />
            )}

            {!isMealItemAvailable && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  p: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('restaurant.item-unavailable')}
                </Typography>
              </Box>
            )}

            {isMealItemAvailable && (
              <Box sx={{ position: 'absolute', right: 4, bottom: 12 }}>
                <QuantitySelector
                  quantity={quantity}
                  onQuantityChange={handleQuantityChange}
                  hasCustomizations={!!mealItem.customizations?.length}
                />
              </Box>
            )}
          </Box>
        </ListItem>

        <MealItemCustomizationSelector
          open={showCustomizations}
          onClose={() => setShowCustomizations(false)}
          customizations={mealItem.customizations || []}
          onAddToCart={handleAddCustomizations}
          itemName={mealItem.name}
          itemPrice={mealItem.price}
          mealItemId={mealItem.id}
        />
      </>
    );
  };

  const filteredMenus = restaurant?.menus
    ?.filter((menu) => {
      return isMenuEnabledForToday(menu.availabilitySchedules);
    })
    .sort((menuA, menuB) => {
      // Sort by availability (available menus first)
      const isMenuAAvailable = availableMenus.includes(menuA.id);
      const isMenuBAvailable = availableMenus.includes(menuB.id);

      if (isMenuAAvailable && !isMenuBAvailable) return -1;
      if (!isMenuAAvailable && isMenuBAvailable) return 1;
      return 0;
    });

  const renderMealItems = (mealItems: MealItem[], menu: Menu) => {
    // Sort meal items - available ones first, unavailable ones last
    const sortedMealItems = [...mealItems].sort((a, b) => {
      const isItemAAvailable =
        availableMenus.includes(menu.id) && a.isAvailable !== false;
      const isItemBAvailable =
        availableMenus.includes(menu.id) && b.isAvailable !== false;

      if (isItemAAvailable && !isItemBAvailable) return -1;
      if (!isItemAAvailable && isItemBAvailable) return 1;
      return 0;
    });

    if (isMobile) {
      return (
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {sortedMealItems.map((mealItem) => (
            <ListViewMealItem
              key={mealItem.id}
              mealItem={mealItem}
              isAvailable={availableMenus.includes(menu.id)}
            />
          ))}
        </List>
      );
    }

    return (
      <Grid container spacing={3}>
        {sortedMealItems.map((mealItem) => (
          <Grid key={mealItem.id} item xs={12} sm={6} md={4} lg={3} xl={2}>
            <MealItemCard
              quantity={cart.orderItems
                .filter((item) => item.mealItemId === mealItem.id)
                .reduce((sum, item) => sum + item.quantity, 0)}
              mealItem={mealItem}
              onQuantityChange={(cartItemId, quantity, customizations) =>
                onQuantityChange(cartItemId, mealItem, quantity, customizations)
              }
              isAvailable={
                availableMenus.includes(menu.id) &&
                mealItem.isAvailable !== false
              }
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  if (status.includes('pending')) return <LoadingComponent />;

  if (!restaurant)
    return (
      <>
        <MetaTags
          title={t('restaurant.not-found')}
          description={t('restaurant.not-found-description')}
          imageUrl={'https://smartristo.ch/assets/images/smartristo-banner.jpg'}
        />{' '}
        <Typography variant="h4">{t('restaurant.not-found')}</Typography>
      </>
    );

  return (
    <>
      <MetaTags
        title={restaurant.name}
        description={restaurant.description || ''}
        imageUrl={
          restaurant.bannerUrl
            ? restaurant.bannerUrl.startsWith('http')
              ? restaurant.bannerUrl
              : `https://smartristo.ch${restaurant.bannerUrl}`
            : 'https://smartristo.ch/assets/images/smartristo-banner.jpg'
        }
      />
      <Container
        maxWidth={false}
        sx={{
          pt: 2,
          px: 0,
          pb: 0,
        }}
      >
        {/* Restaurant name bar for mobile (non-sticky) */}
        {isMobile && (
          // <Paper elevation={2} sx={{ mb: 2 }}>
          <Toolbar
            sx={{
              pt: 0,
              px: 0,
              pb: 0.5,
              m: 0,
              minHeight: 'auto',
            }}
            disableGutters
          >
            <Typography
              variant="h6"
              component="div"
              noWrap
              sx={{ flexGrow: 1 }}
            >
              {restaurant.name}
            </Typography>
            {/* Review section - commented out temporarily
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Rating
                  value={restaurant.rating ?? 0}
                  readOnly
                  precision={0.5}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  ({restaurant.reviewCount ?? 0})
                </Typography>
              </Box>
              */}
          </Toolbar>
          // </Paper>
        )}

        {/* Restaurant Header - Only show on non-mobile */}
        {!isMobile && (
          <Paper elevation={3} sx={{ mb: 2 }}>
            {/* Banner Image - Only show on non-mobile */}
            {restaurant.bannerUrl && (
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '240px',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={restaurant.bannerUrl}
                    alt={restaurant.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Title Section */}
            <Box sx={{ px: 2, pb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 500 }}>
                {restaurant.name}
              </Typography>

              {/* Review section - commented out temporarily
              <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                <Rating
                  value={restaurant.rating ?? 0}
                  readOnly
                  precision={0.5}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  ({restaurant.reviewCount ?? 0} {t('restaurant.reviews')})
                </Typography>
              </Box>
              */}
            </Box>
            {/* This line would be uncommented when you want to use the component */}
            {/* <RestaurantLocationAndHours
              restaurant={restaurant}
              onAddressClick={handleAddressClick}
            /> */}
          </Paper>
        )}

        {/* Menu Tabs */}
        <Paper elevation={3} sx={{ p: 0.25, mb: 2 }}>
          <TabContext value={selectedMenu}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={selectedMenu}
                onChange={handleChange}
                aria-label={t('restaurant.menu')}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    px: isMobile ? 1 : 3,
                    py: 1,
                  },
                }}
              >
                {filteredMenus?.map((menu, index) => {
                  return (
                    <Tab
                      key={menu.id}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'top',
                            gap: 0.5,
                            height: '100%',
                          }}
                        >
                          <Typography variant="subtitle1">
                            {menu.title}
                          </Typography>
                          <Chip
                            label={t('restaurant.menu-items-count', {
                              count: menu.mealItems.length,
                            })}
                            size="small"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', textAlign: 'center' }}
                          >
                            {formatMenuTime(menu.availabilitySchedules)}
                          </Typography>
                        </Box>
                      }
                      value={`${index}`}
                    />
                  );
                })}
              </Tabs>
            </Box>

            {filteredMenus?.map((menu, index) => (
              <TabPanel
                sx={{ p: isMobile ? 1 : 2 }}
                key={menu.id}
                value={`${index}`}
              >
                {renderMealItems(menu.mealItems, menu)}
              </TabPanel>
            ))}
          </TabContext>
        </Paper>
      </Container>
    </>
  );
};

export default CustomerRestaurantPage;
